/**
 * The shapes Firestore holds, and the conversion to and from the types the
 * settlement engine works in.
 *
 * This lives in core because three places read a ledger row: the function that
 * settles a payment, the screen that shows the resident the same figure, and
 * the tools. A second parser is a second opinion, and the whole reason this
 * package exists is that there is only one.
 *
 * Everything crossing this boundary is checked. A ledger entry that has lost
 * its period, or an amount that arrived as a decimal, is a corrupt book rather
 * than a slightly wrong screen, so this throws instead of guessing.
 */

import type { CampaignDoc, CampaignStatus } from "./campaigns.js";
import { CAMPAIGN_STATUSES } from "./campaigns.js";
import type { LedgerEntry, PeriodSummary } from "./ledger.js";
import { type Birr, assertBirr } from "./money.js";
import { type Period, assertPeriod } from "./period.js";

export class DocumentError extends Error {}

export interface UnitDoc {
  orgId: string;
  buildingId: string;
  /** The block on the door, which is how the accountant's map is grouped. */
  block: string;
  monthlyDue: Birr;
  currentTermId: string;
  active: boolean;
  billingPaused: boolean;
}

/**
 * How the money moved. Telebirr is modelled because the payment record has to
 * hold one the day merchant access arrives, not because the app can take one.
 * Chapa payments are written only by the function that confirmed them.
 */
export const PAYMENT_METHODS = ["bank_transfer", "telebirr", "chapa", "cash"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export interface PaymentDoc {
  unitId: string;
  termId: string;
  buildingId: string;
  amount: Birr;
  status: "submitted" | "verified" | "rejected";
  /** Future months the resident asked to cover, chosen when they submitted. */
  monthsAhead: number;
  submittedBy: string;
  method: PaymentMethod;
  /**
   * The bank's own transaction id, off the receipt. It is unique per transfer,
   * which is what makes it the one thing on a screenshot worth checking: an
   * image can be edited, a transaction id can be looked up.
   */
  reference: string | null;
  /** Where the screenshot lives, or null for money handed over in person. */
  receiptPath: string | null;
  /**
   * The campaign this money is for, or null for rent.
   *
   * This is what decides how verification treats the payment, so it is read
   * here rather than inferred later: money for a campaign settles no month and
   * never touches a rent balance, and money for rent belongs to no campaign.
   */
  campaignId: string | null;
}

/**
 * A reference is compared, so it is compared in one shape.
 *
 * Bank ids are printed in capitals and typed however the phone felt like it,
 * and two spellings of one transfer would let the same receipt through twice.
 */
export function normalizeReference(reference: string): string {
  return reference.trim().toUpperCase().replace(/\s+/g, "");
}

/**
 * A reference is used as a document id, which is how a bank transfer is claimed
 * exactly once. So it has to be one: no slashes, and short enough to read off a
 * screenshot rather than a paragraph somebody pasted.
 */
export function isReference(value: string): boolean {
  const normalized = normalizeReference(value);
  return (
    normalized.length >= 4 &&
    normalized.length <= 64 &&
    !normalized.includes("/") &&
    normalized !== "." &&
    normalized !== ".."
  );
}

/** Where a bank transfer is claimed. One document per transfer, ever. */
export const referenceId = (reference: string): string => normalizeReference(reference);

export const invoiceId = (unitId: string, period: Period): string => `${unitId}_${period}`;
export const rollupId = (unitId: string, period: Period): string => `${unitId}_${period}`;

/**
 * Ledger ids are derived, never random, so replaying a run writes nothing new.
 *
 * A month can be billed more than once: voiding an invoice reverses its charge
 * and the monthly run raises it again, and the new charge cannot land on the id
 * of the one that was reversed or the reversal would go on cancelling it. The
 * invoice document carries the generation, so the id stays derived and two runs
 * racing still write the same document.
 */
export const invoiceEntryId = (unitId: string, period: Period, generation = 0): string =>
  generation > 0 ? `inv_${unitId}_${period}_g${String(generation)}` : `inv_${unitId}_${period}`;

/** How many times this unit and month have been billed, from the invoice document. */
export function generationOf(data: { get: (field: string) => unknown } | undefined): number {
  const value = data?.get("generation");
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : 0;
}
export const paymentEntryId = (paymentId: string, period: Period): string =>
  `pay_${paymentId}_${period}`;

/**
 * Which months a payment actually settled, read back off the ids it wrote.
 *
 * The screen that tells a resident what their money covered must not work this
 * out by dividing the amount by the monthly due. Those two disagree the moment
 * an accountant adjusts a month, and the ledger is the one that is true. The
 * entry ids are derived rather than random, so the answer is already in the
 * entries the screen has loaded and costs no extra read.
 *
 * A reversed payment settles nothing: its credits are cancelled by entries of
 * their own, and a month whose credit has been taken back is owed again. So a
 * period whose credit carries a reversal is left out rather than reported as
 * covered.
 */
export function periodsSettledBy(
  entries: readonly { id: string; period: Period | null; reverses?: string | null }[],
  paymentId: string,
): Period[] {
  const prefix = `pay_${paymentId}_`;
  const cancelled = new Set(
    entries.map((entry) => entry.reverses).filter((id): id is string => typeof id === "string"),
  );

  return entries
    .filter(
      (entry) => entry.period !== null && entry.id.startsWith(prefix) && !cancelled.has(entry.id),
    )
    .map((entry) => entry.period as Period)
    .sort();
}

/**
 * What became of money a payment brought beyond the months it could settle.
 *
 * Verification opens one of these, `held_<paymentId>`, rather than crediting
 * months nobody asked to pay. The accountant then refunds it, waives it or
 * puts it against an owed month. The resident is told which in their own
 * history, because it is their money and the accountant's queue was the only
 * place it showed.
 */
export const HELD_STATUSES = ["unresolved", "refunded", "waived", "extended"] as const;
export type HeldStatus = (typeof HELD_STATUSES)[number];

export interface HeldDoc {
  /** The payment that brought it, or null for a row without that link. */
  paymentId: string | null;
  amount: Birr;
  status: HeldStatus;
  /** The month it was put against, when the accountant extended it. */
  appliedTo: Period | null;
}

export function toHeld(data: Record<string, unknown>): HeldDoc {
  const { paymentId, appliedTo } = data;
  let period: Period | null = null;
  if (appliedTo !== undefined && appliedTo !== null) {
    if (typeof appliedTo !== "string") {
      throw new DocumentError(`Expected appliedTo to be a period, received ${typeof appliedTo}`);
    }
    assertPeriod(appliedTo);
    period = appliedTo;
  }
  return {
    paymentId: typeof paymentId === "string" && paymentId !== "" ? paymentId : null,
    amount: requireBirr(data, "amount"),
    status: requireOneOf(data, "status", HELD_STATUSES),
    appliedTo: period,
  };
}

/** The extra a payment brought, if it brought any. */
export function heldFor(rows: readonly HeldDoc[], paymentId: string): HeldDoc | null {
  return rows.find((row) => row.paymentId === paymentId) ?? null;
}

/** Money still waiting on the accountant, which is what the balance counts as held. */
export function unresolvedHeld(rows: readonly HeldDoc[]): Birr {
  return rows
    .filter((row) => row.status === "unresolved")
    .reduce((total, row) => total + row.amount, 0);
}

/** Everything except users lives under an organization. */
export function orgCollection(orgId: string, name: string): string {
  return `organizations/${orgId}/${name}`;
}

function requireString(data: Record<string, unknown>, field: string): string {
  const value = data[field];
  if (typeof value !== "string" || value === "") {
    throw new DocumentError(`Expected a non-empty ${field}, received ${JSON.stringify(value)}`);
  }
  return value;
}

/**
 * Narrowing by exclusion does not work on a value declared as a string, so the
 * allowed set is passed in and the result carries the narrow type.
 */
function requireOneOf<T extends string>(
  data: Record<string, unknown>,
  field: string,
  allowed: readonly T[],
): T {
  const value = requireString(data, field);
  if (!(allowed as readonly string[]).includes(value)) {
    throw new DocumentError(
      `Expected ${field} to be one of ${allowed.join(", ")}, received "${value}"`,
    );
  }
  return value as T;
}

/** A projected total, which is absent until the function has written one. */
function optionalBirr(data: Record<string, unknown>, field: string): Birr {
  return data[field] === undefined ? 0 : requireBirr(data, field);
}

function optionalCount(data: Record<string, unknown>, field: string): number {
  const value = data[field];
  if (value === undefined) {
    return 0;
  }
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new DocumentError(`Expected ${field} to be a count, received ${JSON.stringify(value)}`);
  }
  return value;
}

function requireBirr(data: Record<string, unknown>, field: string): Birr {
  const value = data[field];
  if (typeof value !== "number") {
    throw new DocumentError(`Expected ${field} to be a number, received ${typeof value}`);
  }
  assertBirr(value, field);
  return value;
}

function requireBoolean(data: Record<string, unknown>, field: string): boolean {
  const value = data[field];
  if (typeof value !== "boolean") {
    throw new DocumentError(`Expected ${field} to be a boolean, received ${typeof value}`);
  }
  return value;
}

function isTimestamp(value: unknown): value is { toDate: () => Date } {
  return (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate: unknown }).toDate === "function"
  );
}

/**
 * A date arrives as a Timestamp from either Firebase SDK, as a Date from a
 * seed, and as a string from anything that has been through JSON. The two SDKs
 * ship different Timestamp classes, so this asks what the value can do rather
 * than which class it came from.
 */
function requireDate(data: Record<string, unknown>, field: string): string {
  const value = data[field];
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (isTimestamp(value)) {
    return value.toDate().toISOString();
  }
  if (typeof value === "string" && !Number.isNaN(Date.parse(value))) {
    return new Date(value).toISOString();
  }
  throw new DocumentError(`Expected ${field} to be a date, received ${JSON.stringify(value)}`);
}

export function toUnit(data: Record<string, unknown>): UnitDoc {
  return {
    orgId: requireString(data, "orgId"),
    buildingId: requireString(data, "buildingId"),
    block: requireString(data, "block"),
    monthlyDue: requireBirr(data, "monthlyDue"),
    currentTermId: requireString(data, "currentTermId"),
    active: requireBoolean(data, "active"),
    billingPaused: requireBoolean(data, "billingPaused"),
  };
}

export function toPayment(data: Record<string, unknown>): PaymentDoc {
  const status = requireOneOf(data, "status", ["submitted", "verified", "rejected"] as const);
  const monthsAhead = data.monthsAhead ?? 0;
  if (typeof monthsAhead !== "number" || !Number.isInteger(monthsAhead) || monthsAhead < 0) {
    throw new DocumentError(`monthsAhead must not be negative, received ${String(monthsAhead)}`);
  }
  const reference = data.reference;
  const receiptPath = data.receiptPath;
  const campaignId = data.campaignId;

  return {
    unitId: requireString(data, "unitId"),
    termId: requireString(data, "termId"),
    buildingId: requireString(data, "buildingId"),
    amount: requireBirr(data, "amount"),
    status,
    monthsAhead,
    submittedBy: requireString(data, "submittedBy"),
    // Cash predates any of this and was recorded before the field existed, so
    // an absent method is the one that needs no receipt.
    method: data.method === undefined ? "cash" : requireOneOf(data, "method", PAYMENT_METHODS),
    reference:
      typeof reference === "string" && reference !== "" ? normalizeReference(reference) : null,
    receiptPath: typeof receiptPath === "string" && receiptPath !== "" ? receiptPath : null,
    campaignId: typeof campaignId === "string" && campaignId !== "" ? campaignId : null,
  };
}

/**
 * A campaign, as the committee wrote it and the function has since counted it.
 *
 * raised and givers are a projection: a campaign that has never had a
 * contribution has no such fields yet, and reading them as zero is correct
 * rather than lenient. Everything the committee typed is required, because a
 * campaign with no target cannot be drawn, cannot be finished, and cannot be
 * explained at a meeting.
 */
export function toCampaign(data: Record<string, unknown>): CampaignDoc {
  const purpose = data.purpose;
  const closesAt = data.closesAt;

  return {
    orgId: requireString(data, "orgId"),
    buildingId: requireString(data, "buildingId"),
    title: requireString(data, "title"),
    purpose: typeof purpose === "string" && purpose !== "" ? purpose : null,
    target: requireBirr(data, "target"),
    status: requireOneOf<CampaignStatus>(data, "status", CAMPAIGN_STATUSES),
    closesAt: closesAt === undefined || closesAt === null ? null : requireDate(data, "closesAt"),
    raised: optionalBirr(data, "raised"),
    givers: optionalCount(data, "givers"),
  };
}

export function toLedgerEntry(id: string, data: Record<string, unknown>): LedgerEntry {
  const type = requireOneOf(data, "type", ["charge", "credit", "adjustment"] as const);
  const source = requireOneOf(data, "source", ["invoice", "payment", "manual"] as const);

  const base = {
    id,
    unitId: requireString(data, "unitId"),
    termId: requireString(data, "termId"),
    date: requireDate(data, "date"),
    type,
    amount: requireBirr(data, "amount"),
    source,
    createdBy: requireString(data, "createdBy"),
    ...(typeof data.reverses === "string" ? { reverses: data.reverses } : {}),
    ...(typeof data.note === "string" ? { note: data.note } : {}),
  };

  const bucket = requireOneOf(data, "bucket", ["rent", "contribution"] as const);
  if (bucket === "contribution") {
    return { ...base, bucket, period: null, campaignId: requireString(data, "campaignId") };
  }

  const period = data.period;
  if (typeof period !== "string") {
    throw new DocumentError(`Rent entry ${id} has no period`);
  }
  assertPeriod(period);
  return { ...base, bucket, period };
}

/**
 * One month of one unit, as the projection stores it.
 *
 * Checked like everything else crossing this boundary. A rollup with a missing
 * figure would draw a tile the accountant would act on, and a wrong tile on the
 * unit map is a month nobody chases.
 */
export function toRollup(data: Record<string, unknown>): {
  unitId: string;
  summary: PeriodSummary;
} {
  const period = requireString(data, "period");
  assertPeriod(period);

  return {
    unitId: requireString(data, "unitId"),
    summary: {
      period,
      charged: requireBirr(data, "charged"),
      credited: requireBirr(data, "credited"),
      balance: requireBirr(data, "closingBalance"),
      state: requireOneOf(data, "state", ["unpaid", "partial", "verified"] as const),
    },
  };
}
